// What the creative builder edits, and the gate that will not let it confirm
// while a required field is empty. Pure: the builder (screens/creative.tsx) and
// its channel editors render these shapes; this module only reads them.
//
// The field keys are the anchors `RequiredField` registers, so a refused field is
// scrolled to and named where it is drawn.

import type { AssetAnimation } from '@/api/types.gen';
import type { DisplaySize } from '@/lib/media-validation/display';
import type { MetaMediaValidationReport } from '@/lib/media-validation/meta-validator';
import {
  CHANNEL_FORMATS,
  isValidCreativeDestinationUrl,
  isValidMetaDisplayUrl,
  metaCopyIssues,
  metaDefaults,
  type CreativeFieldIssue,
  type CreativeFieldValues,
} from '@/screens/creative-config';

/** The library creative a slot was picked from, pinned at the revision it flies. */
export interface CreativeLibraryPin {
  id: string;
  name: string;
  revision: number;
}

/** One creative of a campaign or a new-creative draft, as the builder edits it. */
export interface CreativeSlot {
  id: string;
  label: string;
  /** The operator's ad name; absent means the default built from the production. */
  adName?: string;
  fromLibrary?: CreativeLibraryPin;
  /** A campaign pin whose creative could not be read; the campaign save refuses while one is. */
  unresolved?: boolean;
}

/** A Meta slot also carries its copy, keyed by `CHANNEL_FIELDS.meta` field. */
export interface MetaCreativeSlot extends CreativeSlot {
  metaFields?: CreativeFieldValues;
}

/** The production a creative is built for: its id names uploads, its title the ad. */
export interface CreativeBuilderProduction {
  id: string;
  title: string;
}

/**
 * One media file in a slot: a file the operator just picked (it carries `file`
 * and, for Meta, the validation report), or a presentation-only preview of a
 * library asset (`previewOnly`, no `file`). A `LibraryPreviewAsset` and a
 * `LocalCreativeAsset` both satisfy it.
 */
export interface CreativeSlotAsset {
  type: 'image' | 'video';
  name: string;
  url: string;
  file?: File;
  mediaKind?: 'image' | 'video';
  width?: number | null;
  height?: number | null;
  w?: number | null;
  h?: number | null;
  durationMs?: number | null;
  /** Seconds. CTV stores the catalog duration the file matched (15 or 30). */
  duration?: number | null;
  validation?: MetaMediaValidationReport;
  previewOnly?: true;
}

/** A Meta slot's media keyed by format (`sq`, `fs`, `story`); `null` is an empty format. */
export type MetaSlotAssets = Record<string, CreativeSlotAsset | null>;

/** Every Meta slot's media, keyed by slot id. */
export type MetaAssetsBySlot = Record<string, MetaSlotAssets>;

export interface CtvSlotData {
  destUrl: string;
  asset: CreativeSlotAsset | null;
}

/** One Display banner, picked in this browser (`file`) or a library preview
 *  (`byteSize`, no `file`). A `LibraryBanner` satisfies it. */
export interface DspBanner {
  id: string;
  type: 'image' | 'video';
  name: string;
  url: string;
  file?: File;
  size: DisplaySize;
  width: number;
  height: number;
  w: number;
  h: number;
  animation: AssetAnimation | null;
  byteSize?: number;
}

export interface DspSlotData {
  destUrl: string;
  banners: DspBanner[];
}

export const metaAssetFieldKey = (creativeId: string, formatKey: string) =>
  `creative-meta-${creativeId}-asset-${formatKey}`;
export const metaDestinationFieldKey = (creativeId: string) =>
  `creative-meta-${creativeId}-destination`;
export const metaDisplayUrlFieldKey = (creativeId: string) =>
  `creative-meta-${creativeId}-display-url`;
/** Anchor for a copy field the API requires (post_text / headline / cta). */
export const metaCopyFieldKey = (creativeId: string, fieldKey: string) =>
  `creative-meta-${creativeId}-${fieldKey}`;
export const ctvDestinationFieldKey = (creativeId: string) =>
  `creative-ctv-${creativeId}-destination`;
export const ctvAssetFieldKey = (creativeId: string) =>
  `creative-ctv-${creativeId}-asset`;
export const dspDestinationFieldKey = (groupId: string) =>
  `creative-dsp-${groupId}-destination`;
export const dspAssetFieldKey = (groupId: string) =>
  `creative-dsp-${groupId}-asset`;
export const creativeCollectionFieldKey = (channel: string) =>
  `creative-${channel}-collection`;

/** The format a missing-assets refusal anchors to: the first one drawn. */
const FIRST_META_FORMAT_KEY =
  CHANNEL_FORMATS.meta.formats.map((format) => format.key || format.name)[0] ?? 'sq';

export interface CreativeRequiredInput {
  /** Any channel string; one that is not a creative channel is refused. */
  channel: string | null | undefined;
  production: { title: string } | null | undefined;
  creatives?: readonly MetaCreativeSlot[];
  metaAssets?: MetaAssetsBySlot;
  ctvCreatives?: readonly CreativeSlot[];
  ctvData?: Record<string, CtvSlotData>;
  dspGroups?: readonly CreativeSlot[];
  dspData?: Record<string, DspSlotData>;
}

const isIssue = (issue: CreativeFieldIssue | false): issue is CreativeFieldIssue => Boolean(issue);

export function creativeRequiredIssues({
  channel,
  production,
  creatives = [],
  metaAssets,
  ctvCreatives = [],
  ctvData,
  dspGroups = [],
  dspData,
}: CreativeRequiredInput): CreativeFieldIssue[] {
  if (channel === 'meta') {
    if (creatives.length === 0) {
      return [{
        key: creativeCollectionFieldKey(channel),
        message: 'Add at least one Meta creative before continuing.',
      }];
    }
    return creatives.flatMap((creative, creativeIndex) => {
      const defaults = metaDefaults(
        production,
        channel,
        CHANNEL_FORMATS.meta.name,
        creativeIndex === 0 ? '' : creative.label,
      );
      const values = { ...defaults, ...(creative.metaFields || {}) };
      // The API requires post_text, headline and a CTA from the allowlist. Only
      // the two URLs were gated here, so clearing a prefilled headline sailed
      // through to a server 422 with nothing to anchor it to.
      const copyIssues = metaCopyIssues(values)
        .filter((issue) => issue.key !== 'display_url')
        .map((issue) => ({
          key: metaCopyFieldKey(creative.id, issue.key),
          message: `${issue.message.replace(/\.$/, '')} for ${creative.label}.`,
        }));
      return [
        !isValidCreativeDestinationUrl(values.dest_url) && {
          key: metaDestinationFieldKey(creative.id),
          message: `Enter a valid HTTPS destination URL for ${creative.label}.`,
        },
        !isValidMetaDisplayUrl(values.display_url) && {
          key: metaDisplayUrlFieldKey(creative.id),
          message: `Enter a valid display link for ${creative.label}.`,
        },
        ...copyIssues,
        // Two aspect ratios, not all three. A creative runs on the placements
        // its uploads can serve natively and the ad set is narrowed to match,
        // so demanding a size the client never supplied would only ever be
        // answered by cropping one — which is what this product refuses
        // everywhere else. The floor is two rather than one because Meta
        // requires more than one placement customization rule and this product
        // emits one per aspect ratio; a single-asset creative previews fine and
        // then fails at publish, so it is refused here where it can be fixed.
        ...(CHANNEL_FORMATS.meta.formats.filter((format) =>
          metaAssets?.[creative.id]?.[format.key || format.name]?.url).length >= 2
          ? []
          : [{
              key: metaAssetFieldKey(creative.id, FIRST_META_FORMAT_KEY),
              message: `Upload at least two of the three formats for ${creative.label}. Meta needs more than one to customise placements.`,
            }]),
      ].filter(isIssue);
    });
  }

  if (channel === 'ctv') {
    if (ctvCreatives.length === 0) {
      return [{
        key: creativeCollectionFieldKey(channel),
        message: 'Add at least one CTV creative before continuing.',
      }];
    }
    return ctvCreatives.flatMap((creative) => {
      const current = ctvData?.[creative.id];
      return [
        !isValidCreativeDestinationUrl(current?.destUrl) && {
          key: ctvDestinationFieldKey(creative.id),
          message: `Enter a valid HTTPS destination URL for ${creative.label}.`,
        },
        !current?.asset?.url && {
          key: ctvAssetFieldKey(creative.id),
          message: `Upload the required CTV video for ${creative.label}.`,
        },
      ].filter(isIssue);
    });
  }

  if (channel === 'dsp') {
    if (dspGroups.length === 0) {
      return [{
        key: creativeCollectionFieldKey(channel),
        message: 'Add at least one display banner group before continuing.',
      }];
    }
    return dspGroups.flatMap((group) => {
      const current = dspData?.[group.id];
      return [
        !isValidCreativeDestinationUrl(current?.destUrl) && {
          key: dspDestinationFieldKey(group.id),
          message: `Enter a valid HTTPS destination URL for ${group.label}.`,
        },
        !(current?.banners || []).some((banner) => !!banner.url) && {
          key: dspAssetFieldKey(group.id),
          message: `Upload at least one required banner for ${group.label}.`,
        },
      ].filter(isIssue);
    });
  }

  return [{ key: 'creative-channel', message: 'Select a creative channel before continuing.' }];
}

/** A slot asset's pixel size for the slot card, or a sentence when it is not recorded: a library
 * video saved before sizes were measured has none, and the card used to read "null×null". */
export function assetSizeLabel(asset: { width?: number | null; height?: number | null }): string {
  return asset.width && asset.height ? `${asset.width}×${asset.height}` : 'Size not recorded';
}
